import { ConnectButton } from "@rainbow-me/rainbowkit";
import { Button } from "../ui/button";
import { Wallet, ChevronDown } from "lucide-react";
import { cn } from "../../utils/cn";
import { useBalance } from "wagmi";

interface WalletButtonProps {
  className?: string;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "default" | "lg";
}

export function WalletButton({
  className,
  variant = "default",
  size = "default",
}: WalletButtonProps) {
  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        // Show loading state while mounting
        if (!mounted) {
          return (
            <Button
              variant={variant}
              size={size}
              className={cn("min-w-[140px]", className)}
              disabled
            >
              <Wallet className="w-4 h-4 mr-2" />
              Loading...
            </Button>
          );
        }

        // Show authentication status
        const ready = mounted && authenticationStatus !== "loading";
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated");

        if (!connected) {
          return (
            <Button
              variant={variant}
              size={size}
              className={cn("min-w-[140px]", className)}
              onClick={openConnectModal}
            >
              <Wallet className="w-4 h-4 mr-2" />
              Connect Wallet
            </Button>
          );
        }

        // Show wrong network state
        if (chain.unsupported) {
          return (
            <Button
              variant="destructive"
              size={size}
              className={cn("min-w-[140px]", className)}
              onClick={openChainModal}
            >
              Wrong network
            </Button>
          );
        }

        // Show connected state with account info and balance
        return (
          <WalletConnectedState
            account={account}
            chain={chain}
            openAccountModal={openAccountModal}
            openChainModal={openChainModal}
            variant={variant}
            size={size}
            className={className}
          />
        );
      }}
    </ConnectButton.Custom>
  );
}

// Enhanced connected state component with balance
function WalletConnectedState({
  account,
  chain,
  openAccountModal,
  openChainModal,
  variant,
  size,
  className,
}: {
  account: { address: string; ensName?: string; ensAvatar?: string };
  chain: { name?: string; hasIcon?: boolean; iconUrl?: string };
  openAccountModal: () => void;
  openChainModal: () => void;
  variant: "default" | "outline" | "ghost";
  size: "sm" | "default" | "lg";
  className?: string;
}) {
  const { data: balance } = useBalance({
    address: account.address as `0x${string}`,
  });

  const formatBalance = (bal: string) => {
    const num = parseFloat(bal);
    return num.toFixed(2);
  };

  return (
    <div className="flex items-center gap-4">
      {/* Professional Balance Display */}
      {balance && (
        <div className="flex items-center gap-2 px-3 py-2 bg-white/50 dark:bg-slate-800/50 radius-default border border-gray-200/50 dark:border-slate-700/50 backdrop-blur-sm">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <div className="flex flex-col">
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 leading-tight">
              {formatBalance(balance.formatted)} {balance.symbol}
            </div>
          </div>
        </div>
      )}

      {/* Chain selector */}
      <Button
        variant="outline"
        size={size}
        className="px-3 border-gray-200/50 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-slate-800/80"
        onClick={openChainModal}
      >
        {chain.hasIcon && (
          <div className="w-4 h-4 mr-2">
            {chain.iconUrl && (
              <img
                alt={chain.name ?? "Chain icon"}
                src={chain.iconUrl}
                className="w-4 h-4"
              />
            )}
          </div>
        )}
        {chain.name}
        <ChevronDown className="w-3 h-3 ml-1" />
      </Button>

      {/* Account button */}
      <Button
        variant={variant}
        size={size}
        className={cn(
          "min-w-[120px] bg-primary hover:bg-primary/90 text-primary-foreground border-primary",
          className
        )}
        onClick={openAccountModal}
      >
        {account.ensAvatar && (
          <img
            alt="ENS Avatar"
            src={account.ensAvatar}
            className="w-4 h-4 mr-2 rounded-full"
          />
        )}
        {account.ensName ||
          `${account.address.slice(0, 6)}...${account.address.slice(-4)}`}
        <ChevronDown className="w-3 h-3 ml-2" />
      </Button>
    </div>
  );
}

// Simplified version for mobile or compact layouts
export function WalletButtonCompact({ className }: { className?: string }) {
  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        if (!mounted) {
          return (
            <Button size="sm" variant="outline" className={className} disabled>
              <Wallet className="w-4 h-4" />
            </Button>
          );
        }

        const ready = mounted && authenticationStatus !== "loading";
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated");

        if (!connected) {
          return (
            <Button
              size="sm"
              variant="outline"
              className={className}
              onClick={openConnectModal}
            >
              <Wallet className="w-4 h-4" />
            </Button>
          );
        }

        if (chain.unsupported) {
          return (
            <Button
              size="sm"
              variant="destructive"
              className={className}
              onClick={openChainModal}
            >
              ⚠️
            </Button>
          );
        }

        return (
          <Button
            size="sm"
            variant="outline"
            className={className}
            onClick={openAccountModal}
          >
            {account.ensAvatar ? (
              <img
                alt="ENS Avatar"
                src={account.ensAvatar}
                className="w-4 h-4 rounded-full"
              />
            ) : (
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-purple-500" />
            )}
          </Button>
        );
      }}
    </ConnectButton.Custom>
  );
}
